/* eslint-disable @typescript-eslint/no-explicit-any */
import L from "leaflet";
import type { LatLngTuple, Map as LeafletMap } from "leaflet";
import { MapPin } from "lucide-react";
import moment from "moment";
import { memo, useEffect, useRef, useState } from "react";
import type { ComponentType, CSSProperties, PropsWithChildren } from "react";
import { FeatureGroup, MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import { Fullscreen, FullscreenExit } from "@mui/icons-material";

type LocationPoint = {
    value: any;
    timestamp: number;
};

type MapComponentProps = {
    data: LocationPoint[] | null;
    deviceName: string;
    showHistory?: boolean;
    style?: CSSProperties;
    isFullScreen: boolean;
    setIsFullScreen: (value: boolean) => void;
};

type ClusterGroupProps = PropsWithChildren<{
    animate?: boolean;
    showCoverageOnHover?: boolean;
    iconCreateFunction?: (cluster: any) => L.DivIcon;
}>;

const ClusterGroup = MarkerClusterGroup as unknown as ComponentType<ClusterGroupProps>;

const MapReady = ({ onReady }: { onReady: (map: LeafletMap) => void }) => {
    const map = useMap();

    useEffect(() => {
        onReady(map);
    }, [map, onReady]);

    return null;
};

const createClusterCustomIcon = (cluster: any) => {
    const count = cluster.getChildCount();

    return L.divIcon({
        html: `<div style="
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background-color: rgba(0, 123, 255, 0.8);
                    color: white;
                    font-size: 14px;
                    font-weight: bold;
                    border: 2px solid #ffffff;
                ">
                    ${count}
               </div>`,
        className: "", // Clear default class to remove default styles
        iconSize: [40, 40],
    });
};

const MapComponent = ({ data, deviceName, showHistory = false, style, isFullScreen = false, setIsFullScreen = () => null }: MapComponentProps) => {
    const [map, setMap] = useState<LeafletMap | null>(null);
    const featureGroupRef = useRef<L.FeatureGroup | null>(null);

    useEffect(() => {
        if (!map) return;
        if (!showHistory) return;
        // if data is empty, do not fit bounds
        if (!data || data.length <= 1) return;
        if (!featureGroupRef.current) return;
        map.fitBounds(featureGroupRef.current.getBounds());
    }, [data, map, showHistory]);

    useEffect(() => {
        const handleFullScreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };

        document.addEventListener("fullscreenchange", handleFullScreenChange);

        handleFullScreenChange();

        return () => {
            document.removeEventListener("fullscreenchange", handleFullScreenChange);
        };
    }, [setIsFullScreen]);

    if (!data || data.length === 0) {
        return (
            <div className="flex h-full min-h-32 items-center justify-center bg-white text-sm text-muted-foreground" style={style}>
                No location data
            </div>
        );
    }

    // const { latitude: lat, longitude: long } = data[data.length - 1].value;
    const { latitude: lat, longitude: long } = data[data.length - 1].value;
    const position: LatLngTuple = [Number(lat), Number(long)];

    if (!Number.isFinite(position[0]) || !Number.isFinite(position[1])) {
        return (
            <div className="flex h-full min-h-32 items-center justify-center bg-white text-sm text-muted-foreground" style={style}>
                No location data
            </div>
        );
    }

    const polyline = data.map((item, index) => {
        if (index === 0) {
            return null;
        }

        const { latitude: from_lat, longitude: from_long } = data[index - 1].value;
        const { latitude: to_lat, longitude: to_long } = item.value;
        const from: LatLngTuple = [Number(from_lat), Number(from_long)];
        const to: LatLngTuple = [Number(to_lat), Number(to_long)];

        return (
            <Polyline
                key={`polyline-${index}`}
                positions={[from, to]}
            />
        );
    });

    const markers = showHistory
        ? data.map((item, index) => {
              const { latitude, longitude } = item.value;
              const markerPosition: LatLngTuple = [Number(latitude), Number(longitude)];
              return (
                  <Marker key={`marker-${index}`} position={markerPosition}>
                      <Popup>
                          {deviceName} was seen at {moment(item.timestamp).format("LLL")}
                      </Popup>
                  </Marker>
              );
          })
        : null;

    return (
        <div className="h-full overflow-hidden rounded" style={style}>
            <div className="h-full w-full">
                <MapContainer center={position} zoom={17} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
                    <MapReady onReady={setMap} />
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    {showHistory && (
                        <FeatureGroup ref={featureGroupRef}>
                            {polyline}
                            <ClusterGroup animate={true} showCoverageOnHover={true} iconCreateFunction={createClusterCustomIcon}>
                                {markers}
                            </ClusterGroup>
                        </FeatureGroup>
                    )}
                    <div className="absolute top-2 right-2 z-10" style={{ zIndex: 1000 }}>
                        <button
                            className="p-2 bg-white rounded-full shadow hover:bg-gray-100 transition"
                            onClick={() => {
                                if (!isFullScreen) {
                                    map?.getContainer().requestFullscreen();
                                } else {
                                    document.exitFullscreen();
                                }
                            }}
                            aria-label={isFullScreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                        >
                            {isFullScreen ? <FullscreenExit className="text-gray-700" /> : <Fullscreen className="text-gray-700" />}
                        </button>
                    </div>

                    {/* Recentre button */}
                    <div className="absolute top-16 right-2 z-10" style={{ zIndex: 1000 }}>
                        <button
                            className="p-2 bg-white rounded-full shadow hover:bg-gray-100 transition"
                            onClick={() => {
                                if (map) {
                                    map.setView(position, 17);
                                }
                            }}
                            aria-label="Recenter Map"
                        >
                            <MapPin className="text-gray-700" />
                        </button>
                    </div>

                    <Marker position={position}>
                        <Popup>
                            {deviceName} last seen at {moment(data[data.length - 1].timestamp).format("LLL")}
                            {data[data.length - 1].value?.address ? (
                                <div className="flex items-center mt-2">
                                    <span className="text-xs text-gray-500" style={{ whiteSpace: "nowrap" }}>
                                        Near: {data[data.length - 1].value.address}
                                    </span>
                                </div>
                            ) : null}
                        </Popup>
                    </Marker>
                </MapContainer>
            </div>
        </div>
    );
};

export default memo(MapComponent, (prevProps, nextProps) => {
    return prevProps.data === nextProps.data;
});
